import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Settings2,
  History,
  ArrowDownRight,
  ArrowUpRight,
  ArrowLeftRight,
  BookOpen,
} from "lucide-react";
import AddTimeDialog from "./AddTimeDialog";
import CustomizeAccountsDialog from "./timewatch/CustomizeAccountsDialog";
import MovementDialog from "./timewatch/MovementDialog";
import HistoryDialog from "./timewatch/HistoryDialog";
import TimeWatchWelcomeDialog from "./timewatch/TimeWatchWelcomeDialog";
import TrailerDialog from "./timewatch/TrailerDialog";
import GuideCenter from "./guide/GuideCenter";
import VoiceCommandsGuide from "./guide/VoiceCommandsGuide";
import TimeWatchGuide from "./guide/TimeWatchGuide";
import MitelGuide from "./guide/MitelGuide";
import {
  computeMitelWallet,
  convertEuroToTime,
  formatInt,
  minutesToTimeParts,
  minutesUntilCentenary,
  pmvYearsMonthsDaysUntilCentenary,
  secondsUntilEightPm,
  splitHMS,
} from "../lib/timeMath";
import { useTimeWatchData } from "../lib/useTimeWatchData";

// Small, premium, minimal banknote glyph shown next to each MITEL row.
// Purely visual: renders a stylised note with the denomination.
// It does NOT affect calculations, formulas, or the underlying wallet logic.
function MitelBanknote({ denom }) {
  return (
    <div
      aria-hidden="true"
      data-testid={`mitel-banknote-${denom}`}
      className="relative flex items-center justify-center rounded-[4px] border border-amber-400/40 bg-gradient-to-br from-amber-950/50 via-black/70 to-amber-900/30 shadow-[0_0_14px_rgba(212,175,55,0.18)] overflow-hidden select-none"
      style={{
        width: "clamp(60px, 8vw, 92px)",
        height: "clamp(30px, 4vw, 46px)",
      }}
    >
      {/* Faint inner border for the premium feel */}
      <span
        aria-hidden="true"
        className="absolute inset-[3px] rounded-[2px] border border-amber-400/15 pointer-events-none"
      />
      {/* Corner seals — like a security mark */}
      <span className="absolute top-[3px] left-[5px] text-[7px] font-mono tracking-[0.2em] text-amber-400/60 leading-none">
        M
      </span>
      <span className="absolute bottom-[3px] right-[5px] text-[7px] font-mono tracking-[0.2em] text-amber-400/60 leading-none">
        M
      </span>
      {/* Denomination */}
      <span
        className="seven-seg text-amber-400 text-sm sm:text-base leading-none"
        style={{ textShadow: "0 0 8px rgba(212,175,55,0.45)" }}
      >
        {denom}
      </span>
    </div>
  );
}

// Compact time display used by pages 01, 06, 07.
// variant="full" -> 5 blocks (YYYY.MM.DD.HH.MM)
// variant="ymd"  -> 3 blocks with 3-digit years (YYY.MM.DD)
function TimePartsRow({ parts, testId, variant = "full" }) {
  const allBlocks = [
    {
      key: "years",
      label: "Anni",
      width: variant === "ymd" ? 3 : 4,
      value: parts.years,
    },
    { key: "months", label: "Mesi", width: 2, value: parts.months },
    { key: "days", label: "Giorni", width: 2, value: parts.days },
    { key: "hours", label: "Ore", width: 2, value: parts.hours },
    { key: "minutes", label: "Minuti", width: 2, value: parts.minutes },
  ];
  const blocks = variant === "ymd" ? allBlocks.slice(0, 3) : allBlocks;
  const gridCols = variant === "ymd" ? "grid-cols-3" : "grid-cols-5";
  return (
    <div
      data-testid={testId}
      className={`w-full grid ${gridCols} gap-2 sm:gap-3 md:gap-4`}
    >
      {blocks.map((b) => {
        const active = b.value > 0;
        return (
          <div
            key={b.key}
            data-testid={`${testId}-${b.key}`}
            className={`time-digit-block px-2 py-4 sm:py-6 md:py-8 flex flex-col items-center justify-center ${
              active ? "time-digit-block-active" : ""
            }`}
          >
            <span
              className={`font-mono tracking-wider text-2xl sm:text-4xl md:text-5xl font-light ${
                active ? "text-emerald-400" : "text-emerald-500/30"
              }`}
              style={
                active
                  ? { textShadow: "0 0 24px rgba(16,185,129,0.35)" }
                  : {}
              }
            >
              {String(b.value).padStart(b.width, "0")}
            </span>
            <span className="mt-3 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase text-zinc-500">
              {b.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function TimeWatchPage() {
  // === Central data source (cloud if authenticated, localStorage fallback) ===
  // All economic data (accounts, transactions, DOB, tempoDato, tempoAvuto)
  // lives in the same hook and is auto-synced to MongoDB when logged in.
  const {
    accounts,
    transactions,
    dob,
    tempoDato,
    tempoAvuto,
    totalEuro: euro,
    speseTotali,
    renameAccounts,
    addTransaction,
    deleteTransaction, // eslint-disable-line no-unused-vars -- retained for future admin flows
    updateTransactionNote,
    setDob,
    setTempoDato,
    setTempoAvuto,
    isAuthenticated,
  } = useTimeWatchData();

  const [showAddDato, setShowAddDato] = useState(false);
  const [showAddAvuto, setShowAddAvuto] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [showMovement, setShowMovement] = useState(false);
  const [movementDefaultType, setMovementDefaultType] = useState("in");
  const [showHistory, setShowHistory] = useState(false);

  // === Welcome popup + guide (Libretto di Benvenuto) ===
  // NOTA: L'auto-open al primo accesso è stato rimosso. L'onboarding
  // avviene ora sulla Home. Il Libretto è accessibile dal Centro Guida.
  // Le preferenze `timeWatchIntroDismissed` / `trailerDismissed` /
  // `voiceCommandsDismissed` non sono più utilizzate (checkbox rimosse).
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [welcomeMode, setWelcomeMode] = useState("intro");

  // === Centro Guida (v1) ===
  // Il pulsante "Guida" in alto a destra apre il Centro Guida, che raccoglie
  // tutti i popup informativi (Libretto di Benvenuto, Comandi Vocali, ecc.).
  const [guideCenterOpen, setGuideCenterOpen] = useState(false);
  const [voiceGuideOpen, setVoiceGuideOpen] = useState(false);
  const [timeWatchGuideOpen, setTimeWatchGuideOpen] = useState(false);
  const [mitelGuideOpen, setMitelGuideOpen] = useState(false);

  // === Trailer popup (voce del Centro Guida) ===
  const [trailerOpen, setTrailerOpen] = useState(false);

  // NOTA: l'apertura automatica del Libretto al primo accesso è stata
  // rimossa. L'onboarding di TIME Inside avviene ora tramite l'unico popup
  // presente nella Home (vedi HomeOnboardingDialog). Il Libretto rimane
  // accessibile in qualsiasi momento dal Centro Guida.

  const openGuide = useCallback(() => {
    // Nuovo comportamento: apre il Centro Guida come hub unico.
    setGuideCenterOpen(true);
  }, []);

  // Handler chiamati dalle voci del Centro Guida.
  const openWelcomeFromCenter = useCallback(() => {
    setGuideCenterOpen(false);
    setWelcomeMode("intro");
    setWelcomeOpen(true);
  }, []);

  const openVoiceGuideFromCenter = useCallback(() => {
    setGuideCenterOpen(false);
    setVoiceGuideOpen(true);
  }, []);

  const openTimeWatchGuideFromCenter = useCallback(() => {
    setGuideCenterOpen(false);
    setTimeWatchGuideOpen(true);
  }, []);

  const openMitelGuideFromCenter = useCallback(() => {
    setGuideCenterOpen(false);
    setMitelGuideOpen(true);
  }, []);

  const openTrailerFromCenter = useCallback(() => {
    setGuideCenterOpen(false);
    setTrailerOpen(true);
  }, []);

  // Chiusura del Libretto: chiude semplicemente. La proposta automatica del
  // Trailer come step successivo dell'onboarding è stata rimossa: il Trailer
  // è ora accessibile solo dal Centro Guida.
  const handleWelcomeClose = useCallback(() => {
    setWelcomeOpen(false);
  }, []);

  // Inline add fields for "Offerta" (page 06) and "Ricompensa" (page 07).
  // Two-box input: hours + minutes, accumulated to the persisted total on commit.
  const [datoHours, setDatoHours] = useState("");
  const [datoMinutes, setDatoMinutes] = useState("");
  const [avutoHours, setAvutoHours] = useState("");
  const [avutoMinutes, setAvutoMinutes] = useState("");

  // === Live countdown for page 02 ===
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // === Derived values (euro comes from the central hook) ===
  const time = useMemo(() => convertEuroToTime(euro), [euro]);
  const wallet = useMemo(() => computeMitelWallet(euro), [euro]);

  const centenaryMinutes = useMemo(
    () => minutesUntilCentenary(dob),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dob, nowTick]
  );
  // PMV (Y/M/D) calcolato con la stessa logica di Google Sheets (=DATEDIF
  // calendariale: giorni reali per mese, anni bisestili). Sostituisce il
  // vecchio split fisso 365g/30g che divergeva dal foglio Google.
  const centenaryParts = useMemo(
    () => pmvYearsMonthsDaysUntilCentenary(dob),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dob, nowTick]
  );

  const todayHMS = useMemo(
    () => splitHMS(secondsUntilEightPm()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nowTick]
  );

  const datoParts = useMemo(() => minutesToTimeParts(tempoDato), [tempoDato]);
  const avutoParts = useMemo(() => minutesToTimeParts(tempoAvuto), [tempoAvuto]);

  // === Carousel navigation ===
  const scrollRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_COUNT = 7;

  const goToPage = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(PAGE_COUNT - 1, idx));
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: clamped * el.clientWidth, behavior: "smooth" });
  }, []);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== currentPage) setCurrentPage(idx);
  }, [currentPage]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (
        e.target &&
        (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA")
      )
        return;
      if (e.key === "ArrowRight") goToPage(currentPage + 1);
      else if (e.key === "ArrowLeft") goToPage(currentPage - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [currentPage, goToPage]);

  // Voice / hash navigation — additivo, non modifica alcuna logica esistente.
  // Ascolta:
  //   • URL hash "#page-N" (impostato dall'assistente vocale)
  //   • CustomEvent "time-watch:pmv-go" con detail.page
  // e chiama goToPage(N). Nessun effetto se l'hash non è valido.
  useEffect(() => {
    const applyHash = () => {
      const m = window.location.hash.match(/^#page-(\d+)$/);
      if (!m) return;
      const idx = parseInt(m[1], 10);
      if (Number.isFinite(idx)) {
        requestAnimationFrame(() => goToPage(idx));
      }
    };
    const onPmvGo = (e) => {
      const idx = e?.detail?.page;
      if (Number.isFinite(idx)) {
        requestAnimationFrame(() => goToPage(idx));
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    window.addEventListener("time-watch:pmv-go", onPmvGo);
    return () => {
      window.removeEventListener("hashchange", applyHash);
      window.removeEventListener("time-watch:pmv-go", onPmvGo);
    };
  }, [goToPage]);

  // === Handlers ===
  const openMovement = (type) => {
    setMovementDefaultType(type);
    setShowMovement(true);
  };
  const handleAddDato = (h, m) => setTempoDato((v) => v + h * 60 + m);
  const handleAddAvuto = (h, m) => setTempoAvuto((v) => v + h * 60 + m);

  // Commit two-box (hours + minutes) into the persisted accumulated total.
  const commitDato = () => {
    const h = parseInt(datoHours || "0", 10);
    const m = parseInt(datoMinutes || "0", 10);
    if ((!Number.isFinite(h) || h <= 0) && (!Number.isFinite(m) || m <= 0)) return;
    setTempoDato((v) => v + Math.max(0, h) * 60 + Math.max(0, m));
    setDatoHours("");
    setDatoMinutes("");
  };

  const commitAvuto = () => {
    const h = parseInt(avutoHours || "0", 10);
    const m = parseInt(avutoMinutes || "0", 10);
    if ((!Number.isFinite(h) || h <= 0) && (!Number.isFinite(m) || m <= 0)) return;
    setTempoAvuto((v) => v + Math.max(0, h) * 60 + Math.max(0, m));
    setAvutoHours("");
    setAvutoMinutes("");
  };

  return (
    <>
      <div className="time-bg" aria-hidden="true" />

      {/* Top brand bar */}
      <header
        data-testid="brand-bar"
        className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-6 sm:px-10 py-5 pointer-events-none"
      >
        <div className="flex items-center gap-3 pointer-events-auto">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.9)]" />
          <span
          data-testid="app-title"
            className="text-sm sm:text-base font-light tracking-tight text-white"
          >
            TIME <span className="italic text-zinc-300">Watch</span>
          </span>
        </div>
        <button
          type="button"
          onClick={openGuide}
          data-testid="tw-open-guide"
          aria-label="Apri il Centro Guida di TIME INSIDE"
          className="pointer-events-auto inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-white/10 text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-white/60 hover:text-white hover:border-white/30 backdrop-blur-md bg-black/30 transition-colors"
        >
          <BookOpen className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Guida</span>
        </button>
      </header>

      {/* Carousel */}
      <main
        data-testid="time-watch-carousel"
        ref={scrollRef}
        onScroll={onScroll}
        className="watch-carousel"
      >
        {/* 01 — OPPORTUNITÀ (master design) */}
        <section
          data-testid="page-01"
          className="snap-center shrink-0 w-screen h-screen overflow-y-auto overflow-x-hidden"
        >
          <div className="min-h-full w-full flex flex-col items-center justify-start sm:justify-center px-6 sm:px-10 md:px-16 pt-10 pb-56 sm:py-24">
            <div className="w-full max-w-5xl flex flex-col items-center text-center">
              {/* Icon + section title as a single visual block */}
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <img
                    data-testid="page-01-icon"
                    src="/fingerprint.png"
                    alt=""
                    className="fingerprint-float w-full h-full object-contain select-none"
                    style={{ transform: "scale(1.1)" }}
                    draggable="false"
                  />
                </div>
                <div
                  data-testid="page-01-title"
                  className="mt-1 text-xs sm:text-sm tracking-[0.5em] uppercase text-zinc-300 font-mono"
                >
                  Opportunità
                </div>
              </div>

              {/* TIME question — main focus */}
              <h2
                data-testid="page-01-question"
                className="mt-8 sm:mt-16 text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white leading-[1.1] whitespace-nowrap"
              >
                Tempo per lasciare il segno
              </h2>

              {/* DOB input — minimal (unchanged) */}
              <input
                data-testid="dob-input"
                type="date"
                value={dob}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDob(e.target.value)}
                className="dob-input text-center mt-8 sm:mt-14"
              />

              {/* Value in Seven-Segment digital font */}
              {centenaryParts ? (
                <div
                  data-testid="segno-value"
                  className="mt-10 sm:mt-16 time-value-font text-emerald-400 flex items-center justify-center gap-3 sm:gap-5 md:gap-7 lg:gap-9 flex-nowrap whitespace-nowrap text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
                  style={{
                    textShadow: "0 0 34px rgba(16,185,129,0.44)",
                  }}
                >
                  <span
                    data-testid="segno-years"
                    className="leading-none"
                  >
                    {String(centenaryParts.years).padStart(3, "0")}
                  </span>
                  <span
                    aria-hidden="true"
                    className="inline-block leading-none text-emerald-400"
                    style={{
                      fontFamily: "JetBrains Mono, monospace",
                      fontSize: "0.35em",
                      lineHeight: 1,
                      transform: "translateY(-0.05em)",
                    }}
                  >
                    ●
                  </span>
                  <span
                    data-testid="segno-months"
                    className="leading-none"
                  >
                    {String(centenaryParts.months).padStart(2, "0")}
                  </span>
                  <span
                    aria-hidden="true"
                    className="inline-block leading-none text-emerald-400"
                    style={{
                      fontFamily: "JetBrains Mono, monospace",
                      fontSize: "0.35em",
                      lineHeight: 1,
                      transform: "translateY(-0.05em)",
                    }}
                  >
                    ●
                  </span>
                  <span
                    data-testid="segno-days"
                    className="leading-none"
                  >
                    {String(centenaryParts.days).padStart(2, "0")}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {/* 02 — OGGI (master design) */}
        <section
          data-testid="page-02"
          className="snap-center shrink-0 w-screen h-screen overflow-y-auto overflow-x-hidden"
        >
          <div className="min-h-full w-full flex flex-col items-center justify-start sm:justify-center px-6 sm:px-10 md:px-16 pt-10 pb-56 sm:py-24">
            <div className="w-full max-w-5xl flex flex-col items-center text-center">
              {/* Icon + section title as a single visual block */}
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <img
                    data-testid="page-02-icon"
                    src="/hourglass.png"
                    alt=""
                    className="fingerprint-float w-full h-full object-contain select-none"
                    style={{ transform: "scale(1.1)" }}
                    draggable="false"
                  />
                </div>
                <div
                  data-testid="page-02-title"
                  className="mt-1 text-xs sm:text-sm tracking-[0.5em] uppercase text-zinc-300 font-mono"
                >
                  Oggi
                </div>
              </div>

              {/* TIME question — main focus */}
              <h2
                data-testid="page-02-question"
                className="mt-8 sm:mt-16 text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white leading-[1.1] whitespace-nowrap"
              >
                Tempo a disposizione oggi
              </h2>

              {/* Value in Seven-Segment digital font */}
              <div
                data-testid="today-value"
                className="mt-10 sm:mt-16 time-value-font text-emerald-400 flex items-center justify-center gap-5 sm:gap-7 md:gap-9 text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
                style={{ textShadow: "0 0 34px rgba(16,185,129,0.44)" }}
              >
                <span
                  data-testid="today-hours"
                  className="leading-none"
                >
                  {String(todayHMS.h).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: "0.35em",
                    lineHeight: 1,
                    transform: "translateY(-0.05em)",
                  }}
                >
                  ●
                </span>
                <span
                  data-testid="today-minutes"
                  className="leading-none"
                >
                  {String(todayHMS.m).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: "0.35em",
                    lineHeight: 1,
                    transform: "translateY(-0.05em)",
                  }}
                >
                  ●
                </span>
                <span
                  data-testid="today-seconds"
                  className="leading-none"
                >
                  {String(todayHMS.s).padStart(2, "0")}
                </span>
              </div>

              {/* Labels */}
              <div className="mt-6 grid grid-cols-3 gap-10 sm:gap-16 md:gap-24 text-center">
                {["Ore", "Minuti", "Secondi"].map((label) => (
                  <span
                    key={label}
                    className="text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-zinc-500 font-mono"
                  >
                    {label}
                  </span>
                ))}
              </div>

              {/* Technical note */}
              <p
                data-testid="today-note"
                className="mt-8 text-[10px] tracking-[0.35em] uppercase text-zinc-600 font-mono"
              >
                Fino alle 20:00
              </p>

              {/* Reflection */}
              <p
                data-testid="today-reflection"
                className="mt-10 sm:mt-16 text-sm sm:text-base italic text-zinc-500 max-w-lg leading-relaxed"
              >
                Ogni minuto che passa è una scelta che non torna.
              </p>
            </div>
          </div>
        </section>

        {/* 03 — DENARO (master design) */}
        <section
          data-testid="page-03"
          className="snap-center shrink-0 w-screen h-screen overflow-y-auto overflow-x-hidden"
        >
          <div className="min-h-full w-full flex flex-col items-center justify-start sm:justify-center px-6 sm:px-10 md:px-16 pt-10 pb-56 sm:py-24">
            <div className="w-full max-w-5xl flex flex-col items-center text-center">
              {/* Icon + section title as a single visual block */}
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <img
                    data-testid="page-03-icon"
                    src="/money-bag.png"
                    alt=""
                    className="fingerprint-float w-full h-full object-contain select-none"
                    style={{ transform: "scale(1.15)" }}
                    draggable="false"
                  />
                </div>
                <div
                  data-testid="page-03-title"
                  className="mt-1 text-xs sm:text-sm tracking-[0.5em] uppercase text-zinc-300 font-mono"
                >
                  Patrimonio
                </div>
              </div>

              {/* TIME question — main focus */}
              <h2
                data-testid="page-03-question"
                className="mt-8 sm:mt-16 text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white leading-[1.1] whitespace-nowrap"
              >
                Denaro
              </h2>

              {/* Total money — read-only, auto-computed from accounts */}
              <div
                data-testid="money-total"
                className="mt-10 sm:mt-16 flex items-baseline justify-center gap-2 sm:gap-4"
              >
                <span
                  aria-hidden="true"
                  className={`text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light transition-colors duration-500 ${
                    euro === 0 ? "text-amber-400/20" : "text-amber-400/80"
                  }`}
                >
                  €
                </span>
                <span
                  data-testid="money-total-value"
                  className={`text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tight tabular-nums transition-colors duration-500 ${
                    euro === 0 ? "text-amber-400/20" : "text-amber-400"
                  }`}
                  style={
                    euro === 0
                      ? {}
                      : { textShadow: "0 0 32px rgba(212,175,55,0.35)" }
                  }
                >
                  {formatInt(euro)}
                </span>
              </div>
              <div className="money-underline mt-6" />

              {/* Spese Totali — in rosso, sempre visibile, derivata da transactions */}
              <div
                data-testid="spese-totali"
                className="mt-6 flex items-baseline justify-center gap-2 text-red-400/85"
              >
                <span className="text-[9px] sm:text-[10px] tracking-[0.3em] uppercase font-mono text-red-400/60">
                  Spese totali
                </span>
                <span
                  aria-hidden="true"
                  className="text-sm sm:text-base font-light text-red-400/70 ml-2"
                >
                  €
                </span>
                <span
                  data-testid="spese-totali-value"
                  className="text-sm sm:text-base font-mono tabular-nums text-red-300"
                  style={{ textShadow: "0 0 12px rgba(248,113,113,0.35)" }}
                >
                  {formatInt(speseTotali)}
                </span>
              </div>

              {/* Action bar:
                  Riga 1 → Entrata · Spesa · Switch
                  Riga 2 → Storico · Personalizza                       */}
              <div
                data-testid="money-actions"
                className="mt-8 sm:mt-10 flex flex-col items-center gap-2 sm:gap-3"
              >
                <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                  <button
                    data-testid="add-entrata-btn"
                    type="button"
                    onClick={() => openMovement("in")}
                    className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-emerald-300 border border-emerald-400/40 rounded-full hover:bg-emerald-400/10 hover:border-emerald-400/70 transition-colors font-mono"
                  >
                    <ArrowDownRight className="w-3.5 h-3.5" />
                    Entrata
                  </button>
                  <button
                    data-testid="add-uscita-btn"
                    type="button"
                    onClick={() => openMovement("out")}
                    className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-red-300 border border-red-400/40 rounded-full hover:bg-red-400/10 hover:border-red-400/70 transition-colors font-mono"
                  >
                    <ArrowUpRight className="w-3.5 h-3.5" />
                    Spesa
                  </button>
                  <button
                    data-testid="add-switch-btn"
                    type="button"
                    onClick={() => openMovement("switch")}
                    title="Trasferisci denaro tra due fonti (non modifica il Patrimonio Totale)"
                    className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-sky-300 border border-sky-400/40 rounded-full hover:bg-sky-400/10 hover:border-sky-400/70 transition-colors font-mono"
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5" />
                    Switch
                  </button>
                </div>
                <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
                  <button
                    data-testid="history-btn"
                    type="button"
                    onClick={() => setShowHistory(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-white border border-white/15 rounded-full hover:text-white hover:border-white/30 transition-colors font-mono"
                  >
                    <History className="w-3.5 h-3.5" />
                    Storico
                  </button>
                  <button
                    data-testid="customize-btn"
                    type="button"
                    onClick={() => setShowCustomize(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 sm:px-5 sm:py-2.5 text-[10px] sm:text-[11px] tracking-[0.24em] uppercase text-zinc-400 border border-white/15 rounded-full hover:text-white hover:border-white/30 transition-colors font-mono"
                    title="Personalizza le tue 5 fonti di denaro"
                  >
                    <Settings2 className="w-3.5 h-3.5" />
                    Personalizza
                  </button>
                </div>
              </div>

              {/* Accounts — read-only balances, updated automatically by movements */}
              <div
                data-testid="accounts-list"
                className="mt-8 sm:mt-10 w-full max-w-md flex flex-col"
              >
                {accounts.map((a) => (
                  <div
                    key={a.id}
                    data-testid={`account-row-${a.id}`}
                    className="flex items-center justify-between gap-4 py-3 border-b border-white/5"
                  >
                    <span
                      data-testid={`account-label-${a.id}`}
                      className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-zinc-400 font-mono text-left truncate"
                      title={a.label}
                    >
                      {a.label}
                    </span>
                    <div className="flex items-baseline gap-2 tabular-nums">
                      <span
                        aria-hidden="true"
                        className={`text-base sm:text-lg font-light transition-colors duration-300 ${
                          a.balance === 0 ? "text-amber-400/25" : "text-amber-400/70"
                        }`}
                      >
                        €
                      </span>
                      <span
                        data-testid={`account-balance-${a.id}`}
                        className={`text-lg sm:text-xl font-mono tabular-nums transition-colors duration-300 ${
                          a.balance === 0
                            ? "text-amber-400/25"
                            : a.balance < 0
                            ? "text-red-300"
                            : "text-amber-400"
                        }`}
                      >
                        {a.balance < 0 ? "−" : ""}
                        {formatInt(Math.abs(a.balance))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Sync hint (subtle) */}
              {!isAuthenticated && (
                <p
                  data-testid="sync-hint"
                  className="mt-6 text-[10px] tracking-[0.2em] uppercase text-zinc-600 font-mono max-w-sm"
                >
                  Accedi per sincronizzare i dati su tutti i dispositivi
                </p>
              )}
            </div>
          </div>
        </section>

        {/* 04 — LIBERTÀ (master design, read-only auto-calc from Denaro) */}
        <section
          data-testid="page-04"
          className="snap-center shrink-0 w-screen h-screen overflow-y-auto overflow-x-hidden"
        >
          <div className="min-h-full w-full flex flex-col items-center justify-start sm:justify-center px-6 sm:px-10 md:px-16 pt-10 pb-56 sm:py-24">
            <div className="w-full max-w-5xl flex flex-col items-center text-center">
              {/* Icon + section title as a single visual block */}
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <img
                    data-testid="page-04-icon"
                    src="/rainbow.png"
                    alt=""
                    className="fingerprint-float w-full h-full object-contain select-none"
                    style={{ transform: "scale(1.2)" }}
                    draggable="false"
                  />
                </div>
                <div
                  data-testid="page-04-title"
                  className="mt-1 text-xs sm:text-sm tracking-[0.5em] uppercase text-zinc-300 font-mono"
                >
                  Libertà
                </div>
              </div>

              {/* TIME question — main focus */}
              <h2
                data-testid="page-04-question"
                className="mt-8 sm:mt-16 text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white leading-[1.1] whitespace-nowrap"
              >
                Tempo libero
              </h2>

              {/* Value in Seven-Segment digital font — auto-calculated from Denaro */}
              <div
                data-testid="liberta-value"
                className="mt-10 sm:mt-16 time-value-font text-emerald-400 flex items-center justify-center gap-1.5 sm:gap-3 md:gap-5 lg:gap-6 flex-nowrap whitespace-nowrap text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
                style={{ textShadow: "0 0 34px rgba(16,185,129,0.44)" }}
              >
                <span
                  data-testid="liberta-years"
                  className="leading-none"
                >
                  {String(time.years).padStart(4, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: "0.35em",
                    lineHeight: 1,
                    transform: "translateY(-0.05em)",
                  }}
                >
                  ●
                </span>
                <span
                  data-testid="liberta-months"
                  className="leading-none"
                >
                  {String(time.months).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: "0.35em",
                    lineHeight: 1,
                    transform: "translateY(-0.05em)",
                  }}
                >
                  ●
                </span>
                <span
                  data-testid="liberta-days"
                  className="leading-none"
                >
                  {String(time.days).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: "0.35em",
                    lineHeight: 1,
                    transform: "translateY(-0.05em)",
                  }}
                >
                  ●
                </span>
                <span
                  data-testid="liberta-hours"
                  className="leading-none"
                >
                  {String(time.hours).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: "0.35em",
                    lineHeight: 1,
                    transform: "translateY(-0.05em)",
                  }}
                >
                  ●
                </span>
                <span
                  data-testid="liberta-minutes"
                  className="leading-none"
                >
                  {String(time.minutes).padStart(2, "0")}
                </span>
              </div>

              {/* Labels */}
              <div
                className="mt-5 grid gap-2 sm:gap-4 md:gap-6 text-center w-full max-w-4xl"
                style={{
                  gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                }}
              >
                {["Anni", "Mesi", "Giorni", "Ore", "Minuti"].map((label) => (
                  <span
                    key={label}
                    className="text-[8px] sm:text-[10px] md:text-[11px] tracking-[0.15em] sm:tracking-[0.25em] md:tracking-[0.3em] uppercase text-zinc-500 font-mono"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 05 — PORTAFOGLIO MITEL (master design, read-only auto-calc from Denaro) */}
        <section
          data-testid="page-05"
          className="snap-center shrink-0 w-screen h-screen overflow-y-auto overflow-x-hidden"
        >
          <div className="min-h-full w-full flex flex-col items-center justify-start sm:justify-center px-6 sm:px-10 md:px-16 pt-10 pb-56 sm:py-24">
            <div className="w-full max-w-5xl flex flex-col items-center text-center">
              {/* Icon + section title as a single visual block */}
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <img
                    data-testid="page-05-icon"
                    src="/gold-bar.png"
                    alt=""
                    className="fingerprint-float w-full h-full object-contain select-none"
                    style={{ transform: "scale(0.9)" }}
                    draggable="false"
                  />
                </div>
                <div
                  data-testid="page-05-title"
                  className="mt-1 text-xs sm:text-sm tracking-[0.5em] uppercase text-zinc-300 font-mono"
                >
                  Portafoglio MITEL
                </div>
              </div>

              {/* TIME question — main focus */}
              <h2
                data-testid="page-05-question"
                className="mt-8 sm:mt-16 text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white leading-[1.1] whitespace-nowrap"
              >
                Banconote universali
              </h2>

              {/* Banknote list */}
              {wallet.notes.length === 0 ? (
                <p
                  data-testid="mitel-empty"
                  className="mt-10 sm:mt-14 text-sm text-zinc-500 max-w-sm"
                >
                  Nessun MITEL nel portafoglio. Aggiungi denaro per generare
                  le tue banconote.
                </p>
              ) : (
                <div
                  data-testid="mitel-list"
                  className="mt-8 sm:mt-14 w-full max-w-2xl flex flex-col gap-3 sm:gap-4"
                >
                  {wallet.notes.map((note) => (
                    <div
                      key={note.denom}
                      data-testid={`mitel-row-${note.denom}`}
                      className="grid items-center"
                      style={{
                        gridTemplateColumns: "1fr auto auto auto auto auto 1fr",
                        columnGap: "0.9rem",
                      }}
                    >
                      <span />
                      <span
                        data-testid={`mitel-qty-${note.denom}`}
                        className="seven-seg text-3xl sm:text-4xl md:text-5xl text-amber-400 text-right leading-none"
                        style={{
                          textShadow: "0 0 24px rgba(212,175,55,0.35)",
                        }}
                      >
                        {String(note.count)}
                      </span>
                      <span className="text-2xl sm:text-3xl md:text-4xl text-zinc-500 font-light leading-none">
                        ×
                      </span>
                      <span className="text-[10px] sm:text-xs tracking-[0.4em] uppercase text-zinc-400 font-mono">
                        MITEL
                      </span>
                      <span
                        data-testid={`mitel-denom-${note.denom}`}
                        className="seven-seg text-3xl sm:text-4xl md:text-5xl text-amber-400 text-left leading-none"
                        style={{
                          textShadow: "0 0 24px rgba(212,175,55,0.35)",
                        }}
                      >
                        {formatInt(note.denom)}
                      </span>
                      <MitelBanknote denom={note.denom} />
                      <span />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* 06 — OFFERTA (master design, minute-only inline add, auto-accumulated) */}
        <section
          data-testid="page-06"
          className="snap-center shrink-0 w-screen h-screen overflow-y-auto overflow-x-hidden"
        >
          <div className="min-h-full w-full flex flex-col items-center justify-start sm:justify-center px-6 sm:px-10 md:px-16 pt-10 pb-56 sm:py-24">
            <div className="w-full max-w-5xl flex flex-col items-center text-center">
              {/* Icon + section title as a single visual block */}
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <img
                    data-testid="page-06-icon"
                    src="/hands.png"
                    alt=""
                    className="fingerprint-float w-full h-full object-contain select-none"
                    style={{ transform: "scale(1.1)" }}
                    draggable="false"
                  />
                </div>
                <div
                  data-testid="page-06-title"
                  className="mt-1 text-xs sm:text-sm tracking-[0.5em] uppercase text-zinc-300 font-mono"
                >
                  Offerto
                </div>
              </div>

              {/* TIME question — main focus */}
              <h2
                data-testid="page-06-question"
                className="mt-8 sm:mt-16 text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white leading-[1.1] whitespace-nowrap"
              >
                Tempo dato
              </h2>

              {/* Total in Seven-Segment digital font */}
              <div
                data-testid="dato-value"
                className="mt-10 sm:mt-16 time-value-font text-emerald-400 flex items-center justify-center gap-1.5 sm:gap-3 md:gap-5 lg:gap-6 flex-nowrap whitespace-nowrap text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
                style={{ textShadow: "0 0 34px rgba(16,185,129,0.44)" }}
              >
                <span
                  data-testid="dato-years"
                  className="leading-none"
                >
                  {String(datoParts.years).padStart(4, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.35em", lineHeight: 1, transform: "translateY(-0.05em)" }}
                >
                  ●
                </span>
                <span
                  data-testid="dato-months"
                  className="leading-none"
                >
                  {String(datoParts.months).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.35em", lineHeight: 1, transform: "translateY(-0.05em)" }}
                >
                  ●
                </span>
                <span
                  data-testid="dato-days"
                  className="leading-none"
                >
                  {String(datoParts.days).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.35em", lineHeight: 1, transform: "translateY(-0.05em)" }}
                >
                  ●
                </span>
                <span
                  data-testid="dato-hours"
                  className="leading-none"
                >
                  {String(datoParts.hours).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.35em", lineHeight: 1, transform: "translateY(-0.05em)" }}
                >
                  ●
                </span>
                <span
                  data-testid="dato-minutes"
                  className="leading-none"
                >
                  {String(datoParts.minutes).padStart(2, "0")}
                </span>
              </div>

              {/* Labels */}
              <div
                className="mt-5 grid gap-2 sm:gap-4 md:gap-6 text-center w-full max-w-4xl"
                style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}
              >
                {["Anni", "Mesi", "Giorni", "Ore", "Minuti"].map((label) => (
                  <span
                    key={label}
                    className="text-[8px] sm:text-[10px] md:text-[11px] tracking-[0.15em] sm:tracking-[0.25em] md:tracking-[0.3em] uppercase text-zinc-500 font-mono"
                  >
                    {label}
                  </span>
                ))}
              </div>

              {/* Two-box input (Ore + Minuti) + Aggiungi + Azzera */}
              <div className="mt-10 sm:mt-14 flex flex-col items-center">
                <div className="flex items-baseline gap-6 sm:gap-10">
                  <label className="flex flex-col items-center">
                    <input
                      data-testid="dato-hours-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={datoHours}
                      onChange={(e) =>
                        setDatoHours(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitDato();
                        }
                      }}
                      placeholder="00"
                      className="dob-input text-center w-24 sm:w-28 text-2xl sm:text-3xl"
                      style={{ letterSpacing: "0.08em" }}
                    />
                    <span className="mt-2 text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
                      Ore
                    </span>
                  </label>
                  <label className="flex flex-col items-center">
                    <input
                      data-testid="dato-minutes-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={datoMinutes}
                      onChange={(e) =>
                        setDatoMinutes(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitDato();
                        }
                      }}
                      placeholder="00"
                      className="dob-input text-center w-24 sm:w-28 text-2xl sm:text-3xl"
                      style={{ letterSpacing: "0.08em" }}
                    />
                    <span className="mt-2 text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
                      Minuti
                    </span>
                  </label>
                </div>
                <div className="mt-6 sm:mt-8 flex items-center gap-3 flex-wrap justify-center">
                  <button
                    data-testid="dato-add"
                    type="button"
                    onClick={commitDato}
                    className="px-5 py-2 sm:px-6 sm:py-2.5 text-[11px] sm:text-xs tracking-[0.25em] uppercase text-emerald-400 border border-emerald-400/40 rounded-full hover:bg-emerald-400/10 hover:border-emerald-400/70 transition-colors font-mono"
                  >
                    Aggiungi
                  </button>
                  {tempoDato > 0 && (
                    <button
                      data-testid="dato-reset"
                      type="button"
                      onClick={() => setTempoDato(0)}
                      className="px-4 py-2 text-[11px] tracking-[0.25em] uppercase text-zinc-500 hover:text-white transition-colors font-mono"
                    >
                      Azzera
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 07 — RICOMPENSA (master design, minute-only inline add, auto-accumulated) */}
        <section
          data-testid="page-07"
          className="snap-center shrink-0 w-screen h-screen overflow-y-auto overflow-x-hidden"
        >
          <div className="min-h-full w-full flex flex-col items-center justify-start sm:justify-center px-6 sm:px-10 md:px-16 pt-10 pb-56 sm:py-24">
            <div className="w-full max-w-5xl flex flex-col items-center text-center">
              {/* Icon + section title as a single visual block */}
              <div className="flex flex-col items-center">
                <div className="w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <img
                    data-testid="page-07-icon"
                    src="/heart.png"
                    alt=""
                    className="fingerprint-float w-full h-full object-contain select-none"
                    style={{ transform: "scale(1.05)" }}
                    draggable="false"
                  />
                </div>
                <div
                  data-testid="page-07-title"
                  className="mt-1 text-xs sm:text-sm tracking-[0.5em] uppercase text-zinc-300 font-mono"
                >
                  Riconosciuto
                </div>
              </div>

              {/* TIME question — main focus */}
              <h2
                data-testid="page-07-question"
                className="mt-8 sm:mt-16 text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white leading-[1.1] whitespace-nowrap"
              >
                Tempo avuto
              </h2>

              {/* Total in Seven-Segment digital font */}
              <div
                data-testid="avuto-value"
                className="mt-10 sm:mt-16 time-value-font text-emerald-400 flex items-center justify-center gap-1.5 sm:gap-3 md:gap-5 lg:gap-6 flex-nowrap whitespace-nowrap text-3xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl"
                style={{ textShadow: "0 0 34px rgba(16,185,129,0.44)" }}
              >
                <span
                  data-testid="avuto-years"
                  className="leading-none"
                >
                  {String(avutoParts.years).padStart(4, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.35em", lineHeight: 1, transform: "translateY(-0.05em)" }}
                >
                  ●
                </span>
                <span
                  data-testid="avuto-months"
                  className="leading-none"
                >
                  {String(avutoParts.months).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.35em", lineHeight: 1, transform: "translateY(-0.05em)" }}
                >
                  ●
                </span>
                <span
                  data-testid="avuto-days"
                  className="leading-none"
                >
                  {String(avutoParts.days).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.35em", lineHeight: 1, transform: "translateY(-0.05em)" }}
                >
                  ●
                </span>
                <span
                  data-testid="avuto-hours"
                  className="leading-none"
                >
                  {String(avutoParts.hours).padStart(2, "0")}
                </span>
                <span
                  aria-hidden="true"
                  className="inline-block leading-none text-emerald-400"
                  style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.35em", lineHeight: 1, transform: "translateY(-0.05em)" }}
                >
                  ●
                </span>
                <span
                  data-testid="avuto-minutes"
                  className="leading-none"
                >
                  {String(avutoParts.minutes).padStart(2, "0")}
                </span>
              </div>

              {/* Labels */}
              <div
                className="mt-5 grid gap-2 sm:gap-4 md:gap-6 text-center w-full max-w-4xl"
                style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}
              >
                {["Anni", "Mesi", "Giorni", "Ore", "Minuti"].map((label) => (
                  <span
                    key={label}
                    className="text-[8px] sm:text-[10px] md:text-[11px] tracking-[0.15em] sm:tracking-[0.25em] md:tracking-[0.3em] uppercase text-zinc-500 font-mono"
                  >
                    {label}
                  </span>
                ))}
              </div>

              {/* Two-box input (Ore + Minuti) + Aggiungi + Azzera */}
              <div className="mt-10 sm:mt-14 flex flex-col items-center">
                <div className="flex items-baseline gap-6 sm:gap-10">
                  <label className="flex flex-col items-center">
                    <input
                      data-testid="avuto-hours-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={avutoHours}
                      onChange={(e) =>
                        setAvutoHours(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitAvuto();
                        }
                      }}
                      placeholder="00"
                      className="dob-input text-center w-24 sm:w-28 text-2xl sm:text-3xl"
                      style={{ letterSpacing: "0.08em" }}
                    />
                    <span className="mt-2 text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
                      Ore
                    </span>
                  </label>
                  <label className="flex flex-col items-center">
                    <input
                      data-testid="avuto-minutes-input"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      value={avutoMinutes}
                      onChange={(e) =>
                        setAvutoMinutes(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitAvuto();
                        }
                      }}
                      placeholder="00"
                      className="dob-input text-center w-24 sm:w-28 text-2xl sm:text-3xl"
                      style={{ letterSpacing: "0.08em" }}
                    />
                    <span className="mt-2 text-[9px] sm:text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
                      Minuti
                    </span>
                  </label>
                </div>
                <div className="mt-6 sm:mt-8 flex items-center gap-3 flex-wrap justify-center">
                  <button
                    data-testid="avuto-add"
                    type="button"
                    onClick={commitAvuto}
                    className="px-5 py-2 sm:px-6 sm:py-2.5 text-[11px] sm:text-xs tracking-[0.25em] uppercase text-emerald-400 border border-emerald-400/40 rounded-full hover:bg-emerald-400/10 hover:border-emerald-400/70 transition-colors font-mono"
                  >
                    Aggiungi
                  </button>
                  {tempoAvuto > 0 && (
                    <button
                      data-testid="avuto-reset"
                      type="button"
                      onClick={() => setTempoAvuto(0)}
                      className="px-4 py-2 text-[11px] tracking-[0.25em] uppercase text-zinc-500 hover:text-white transition-colors font-mono"
                    >
                      Azzera
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Bottom nav */}
      <nav
        data-testid="page-nav"
        className="fixed bottom-[76px] sm:bottom-0 left-0 right-0 z-40 flex items-center justify-center gap-5 py-3 sm:py-6 pointer-events-none"
      >
        <button
          data-testid="nav-prev"
          type="button"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 0}
          aria-label="Schermata precedente"
          className="pointer-events-auto w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-white/30 transition-colors disabled:opacity-20 disabled:cursor-not-allowed backdrop-blur-md bg-black/30"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2.5 pointer-events-auto backdrop-blur-md bg-black/30 rounded-full px-5 py-2.5 border border-white/10">
          {Array.from({ length: PAGE_COUNT }).map((_, i) => (
            <button
              key={i}
              data-testid={`nav-dot-${i}`}
              type="button"
              onClick={() => goToPage(i)}
              aria-label={`Vai alla schermata ${i + 1}`}
              className={`nav-dot ${i === currentPage ? "is-active" : ""}`}
            />
          ))}
        </div>

        <button
          data-testid="nav-next"
          type="button"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === PAGE_COUNT - 1}
          aria-label="Schermata successiva"
          className="pointer-events-auto w-10 h-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white hover:border-white/30 transition-colors disabled:opacity-20 disabled:cursor-not-allowed backdrop-blur-md bg-black/30"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </nav>

      {/* Dialogs */}
      <AddTimeDialog
        open={showAddDato}
        onOpenChange={setShowAddDato}
        onAdd={handleAddDato}
        testId="add-dato-dialog"
        title="Aggiungi Tempo Dato"
        description="Quanto Tempo hai regalato? Ore e minuti verranno sommati al totale."
      />
      <AddTimeDialog
        open={showAddAvuto}
        onOpenChange={setShowAddAvuto}
        onAdd={handleAddAvuto}
        testId="add-avuto-dialog"
        title="Aggiungi Tempo Avuto"
        description="Quanto Tempo hai ricevuto? Ore e minuti verranno sommati al totale."
      />

      {/* Sprint 2 dialogs: Personalizza fonti · Movimento · Storico */}
      <CustomizeAccountsDialog
        open={showCustomize}
        onClose={() => setShowCustomize(false)}
        accounts={accounts}
        onSave={(labels) => renameAccounts(labels)}
      />
      <MovementDialog
        open={showMovement}
        onClose={() => setShowMovement(false)}
        accounts={accounts}
        defaultType={movementDefaultType}
        onSave={(m) => addTransaction(m)}
      />
      <HistoryDialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        transactions={transactions}
        accounts={accounts}
        onEditNote={(id, note) => {
          // 1) update ottimistico locale (mantiene sincroni gli stati UI)
          updateTransactionNote(id, note);
          // 2) persistenza cloud tramite endpoint dedicato "solo causale"
          if (isAuthenticated) {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/user-data/transactions/${encodeURIComponent(id)}/note`;
            fetch(url, {
              method: "PATCH",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ note: note || "" }),
            }).catch(() => {
              /* fallback silenzioso: la copia locale resta valida */
            });
          }
        }}
      />

      {/* Libretto di Benvenuto (accessibile dal Centro Guida). */}
      <TimeWatchWelcomeDialog
        open={welcomeOpen}
        initialMode={welcomeMode}
        onClose={handleWelcomeClose}
      />

      {/* Trailer (voce del Centro Guida) */}
      <TrailerDialog
        open={trailerOpen}
        onClose={() => setTrailerOpen(false)}
        onBack={() => {
          setTrailerOpen(false);
          setGuideCenterOpen(true);
        }}
      />

      {/* Centro Guida (v1) — raccoglie tutti i popup informativi */}
      <GuideCenter
        open={guideCenterOpen}
        onClose={() => setGuideCenterOpen(false)}
        entries={[
          {
            id: "welcome-book",
            icon: "book",
            title: "Libretto di Benvenuto",
            description: "Rileggi il libretto di benvenuto e la guida in 4 pagine.",
            onOpen: openWelcomeFromCenter,
          },
          {
            id: "time-watch-guide",
            icon: "watch",
            title: "Guida TIME Watch",
            description: "Impara a utilizzare e comprendere il TIME Watch.",
            onOpen: openTimeWatchGuideFromCenter,
          },
          {
            id: "mitel",
            icon: "coins",
            title: "MITEL",
            description: "La rappresentazione tangibile del Tempo.",
            onOpen: openMitelGuideFromCenter,
          },
          {
            id: "voice-commands",
            icon: "mic",
            title: "Comandi Vocali",
            description: 'Impara a usare l\'Assistente Vocale "Ora TIME".',
            onOpen: openVoiceGuideFromCenter,
          },
          {
            id: "trailer",
            icon: "film",
            title: "Perché un trailer di un film?",
            description: "Il riferimento culturale da cui è nato l'ecosistema TIME.",
            onOpen: openTrailerFromCenter,
          },
        ]}
      />

      <VoiceCommandsGuide
        open={voiceGuideOpen}
        onClose={() => setVoiceGuideOpen(false)}
        onBack={() => {
          setVoiceGuideOpen(false);
          setGuideCenterOpen(true);
        }}
      />

      {/* Guida TIME Watch (Centro Guida — livello 2) */}
      <TimeWatchGuide
        open={timeWatchGuideOpen}
        onClose={() => setTimeWatchGuideOpen(false)}
        onBack={() => {
          setTimeWatchGuideOpen(false);
          setGuideCenterOpen(true);
        }}
      />

      {/* Guida MITEL (Centro Guida — livello 2) */}
      <MitelGuide
        open={mitelGuideOpen}
        onClose={() => setMitelGuideOpen(false)}
        onBack={() => {
          setMitelGuideOpen(false);
          setGuideCenterOpen(true);
        }}
      />
    </>
  );
}
